import { SimpleFilter, SoundTouch, WebAudioBufferSource } from 'soundtouchjs';

type TrackBuildState = 'idle' | 'building' | 'ready' | 'error';
type FadeCurve = 'linear' | 'cosine';

export interface TranslatedTrackSegment {
  id: number;
  url: string;
  startTime: number;
  endTime: number;
}

export interface TranslatedTrackWindow {
  id: number;
  startTime: number;
  endTime: number;
}

export interface TranslatedTrackBuildResult {
  state: TrackBuildState;
  duration: number;
  windowMap: Map<number, TranslatedTrackWindow>;
  segmentCount: number;
}

const DEFAULT_SAMPLE_RATE = 44100;
const DEFAULT_EDGE_FADE_SECONDS = 0;
/** 与左侧视频 seeked 对齐时：时间差小于此值则不重启 BufferSource，避免句边界附近误对齐卡主线程 */
const SEEK_REALIGN_EPSILON_SEC = 0.08;
const SOUND_TOUCH_CHUNK_FRAMES = 4096;
/** A/B 开关：true 使用语音清晰度优先参数；false 使用库默认参数。 */
const USE_HIGH_QUALITY_SPEECH_STRETCH = true;
const HQ_SEQUENCE_MS = 80;
const HQ_SEEK_WINDOW_MS = 35;
const HQ_OVERLAP_MS = 8;
/** 诊断/修复：给输入尾部补静音，促使 SoundTouch 把尾部帧完整排空。 */
const STRETCH_FLUSH_PAD_MS = 420;
const TRAILING_SILENCE_THRESHOLD = 1e-4;
const TRAILING_GUARD_MS = 20;

export interface TranslatedTrackBufferPlayerOptions {
  edgeFadeSeconds?: number;
  fadeCurve?: FadeCurve;
}

const clamp = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, value));
};

const sanitizeTime = (time: number): number => {
  if (!Number.isFinite(time) || time < 0) {
    return 0;
  }

  return time;
};

export class TranslatedTrackBufferPlayer {
  private context: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private masterBuffer: AudioBuffer | null = null;
  private decodedCache = new Map<string, AudioBuffer>();
  private windowMap = new Map<number, TranslatedTrackWindow>();
  private buildToken = 0;
  private playing = false;
  private startContextTime = 0;
  private startOffset = 0;
  private lastKnownOffset = 0;
  private state: TrackBuildState = 'idle';
  private edgeFadeSeconds: number;
  private fadeCurve: FadeCurve;

  public constructor(options?: TranslatedTrackBufferPlayerOptions) {
    this.edgeFadeSeconds = options?.edgeFadeSeconds ?? DEFAULT_EDGE_FADE_SECONDS;
    this.fadeCurve = options?.fadeCurve ?? 'cosine';
  }

  public getState(): TrackBuildState {
    return this.state;
  }

  public getWindowMap(): Map<number, TranslatedTrackWindow> {
    return this.windowMap;
  }

  public getDuration(): number {
    return this.masterBuffer?.duration || 0;
  }

  public isReady(): boolean {
    return this.state === 'ready' && !!this.masterBuffer;
  }

  public isPlaying(): boolean {
    return this.playing;
  }

  public async rebuild(
    segments: TranslatedTrackSegment[],
    totalDuration?: number
  ): Promise<TranslatedTrackBuildResult> {
    const token = ++this.buildToken;
    this.state = 'building';

    const context = await this.ensureContext();
    const normalizedSegments = segments
      .map((segment) => ({
        ...segment,
        startTime: sanitizeTime(segment.startTime),
        endTime: sanitizeTime(segment.endTime),
      }))
      .filter(
        (segment) =>
          !!segment.url &&
          Number.isFinite(segment.startTime) &&
          Number.isFinite(segment.endTime) &&
          segment.endTime > segment.startTime
      )
      .sort((a, b) => a.startTime - b.startTime);

    if (token !== this.buildToken) {
      throw new Error('翻译音轨构建已被新的任务替换');
    }

    const maxEndTime = normalizedSegments.reduce(
      (max, segment) => Math.max(max, segment.endTime),
      0
    );
    const finalDuration = Math.max(sanitizeTime(totalDuration || 0), maxEndTime, 0.001);
    const sampleRate = context.sampleRate || DEFAULT_SAMPLE_RATE;
    const totalFrames = Math.max(1, Math.ceil(finalDuration * sampleRate));

    const decodedEntries = await Promise.all(
      normalizedSegments.map(async (segment) => ({
        segment,
        buffer: await this.getDecodedBuffer(segment.url),
      }))
    );

    if (token !== this.buildToken) {
      throw new Error('翻译音轨构建已被新的任务替换');
    }

    const channelCount = Math.max(
      1,
      ...decodedEntries.map((entry) => Math.min(2, entry.buffer.numberOfChannels || 1))
    );
    const masterBuffer = context.createBuffer(channelCount, totalFrames, sampleRate);
    const masterChannels = Array.from({ length: channelCount }, (_, index) =>
      masterBuffer.getChannelData(index)
    );
    const nextWindowMap = new Map<number, TranslatedTrackWindow>();

    for (const { segment, buffer } of decodedEntries) {
      const startFrame = Math.max(0, Math.floor(segment.startTime * sampleRate));
      const targetFrames = Math.max(
        1,
        Math.ceil((segment.endTime - segment.startTime) * sampleRate)
      );
      const availableFrames = Math.max(0, Math.min(targetFrames, totalFrames - startFrame));

      if (availableFrames <= 0) {
        continue;
      }

      const targetDurationSec = Math.max(availableFrames / sampleRate, 0.001);
      const stretchedBuffer = await this.timeStretchPreservePitch(
        context,
        buffer,
        targetDurationSec
      );

      for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
        const output = masterChannels[channelIndex];
        const inputChannelIndex = Math.min(
          channelIndex,
          Math.max(0, stretchedBuffer.numberOfChannels - 1)
        );
        const input = stretchedBuffer.getChannelData(inputChannelIndex);
        const inputFrames = input.length;

        if (!inputFrames) {
          continue;
        }

        const fadeFrames = Math.min(
          Math.floor(sampleRate * this.edgeFadeSeconds),
          Math.floor(availableFrames / 2)
        );

        const copyFrames = Math.min(availableFrames, inputFrames);
        for (let frame = 0; frame < copyFrames; frame += 1) {
          let sample = input[frame];

          if (fadeFrames > 0) {
            if (frame < fadeFrames) {
              sample *= this.getFadeGain(frame / fadeFrames);
            } else if (frame >= availableFrames - fadeFrames) {
              sample *= this.getFadeGain((availableFrames - frame) / fadeFrames);
            }
          }

          output[startFrame + frame] = sample;
        }
      }

      nextWindowMap.set(segment.id, {
        id: segment.id,
        startTime: segment.startTime,
        endTime: segment.endTime,
      });
    }

    this.pause();
    this.masterBuffer = masterBuffer;
    this.windowMap = nextWindowMap;
    this.lastKnownOffset = clamp(this.lastKnownOffset, 0, masterBuffer.duration);
    this.state = 'ready';

    return {
      state: this.state,
      duration: masterBuffer.duration,
      windowMap: nextWindowMap,
      segmentCount: normalizedSegments.length,
    };
  }

  public async play(time = 0): Promise<void> {
    if (!this.masterBuffer) {
      return;
    }

    const context = await this.ensureContext();
    if (context.state === 'suspended') {
      await context.resume();
    }

    const safeTime = clamp(time, 0, this.masterBuffer.duration);
    this.startSourceAt(safeTime);
  }

  public pause(): void {
    if (this.playing) {
      this.lastKnownOffset = this.getCurrentTime();
    }

    this.stopSource();
    this.playing = false;
  }

  public async seek(time = 0, resume = false): Promise<void> {
    if (!this.masterBuffer) {
      return;
    }

    const safeTime = clamp(time, 0, this.masterBuffer.duration);

    if (
      resume &&
      this.playing &&
      Math.abs(this.getCurrentTime() - safeTime) < SEEK_REALIGN_EPSILON_SEC
    ) {
      return;
    }

    this.lastKnownOffset = safeTime;

    if (!resume || !this.playing) {
      this.stopSource();
      this.playing = false;
      return;
    }

    await this.play(safeTime);
  }

  public stop(): void {
    this.lastKnownOffset = 0;
    this.stopSource();
    this.playing = false;
  }

  public destroy(): void {
    this.stop();
    this.masterBuffer = null;
    this.windowMap.clear();
    this.decodedCache.clear();

    if (this.gainNode) {
      try {
        this.gainNode.disconnect();
      } catch {
        // ignore disconnect errors
      }
    }

    if (this.context) {
      try {
        void this.context.close();
      } catch {
        // ignore close errors
      }
    }

    this.context = null;
    this.gainNode = null;
    this.state = 'idle';
  }

  public getCurrentTime(): number {
    if (!this.masterBuffer) {
      return 0;
    }

    if (!this.playing || !this.context) {
      return clamp(this.lastKnownOffset, 0, this.masterBuffer.duration);
    }

    const elapsed = this.context.currentTime - this.startContextTime;
    return clamp(this.startOffset + elapsed, 0, this.masterBuffer.duration);
  }

  private async getDecodedBuffer(url: string): Promise<AudioBuffer> {
    const cached = this.decodedCache.get(url);
    if (cached) {
      return cached;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`翻译音频下载失败: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const context = await this.ensureContext();
    const decoded = await context.decodeAudioData(arrayBuffer.slice(0));
    this.decodedCache.set(url, decoded);
    return decoded;
  }

  /**
   * 构建期对单条字幕做 time-stretch（tempo-only）：变速但尽量不变调。
   */
  private async timeStretchPreservePitch(
    context: AudioContext,
    input: AudioBuffer,
    targetDurationSec: number
  ): Promise<AudioBuffer> {
    const inputDuration = input.duration || 0;
    if (!Number.isFinite(inputDuration) || inputDuration <= 0) {
      return input;
    }

    const tempo = inputDuration / targetDurationSec;
    if (!Number.isFinite(tempo) || tempo <= 0 || Math.abs(tempo - 1) < 0.001) {
      return input;
    }

    const soundTouch = new SoundTouch();
    if (USE_HIGH_QUALITY_SPEECH_STRETCH) {
      const stAny = soundTouch as unknown as {
        stretch?: {
          setParameters?: (
            sampleRate: number,
            sequenceMs: number,
            seekWindowMs: number,
            overlapMs: number
          ) => void;
        };
      };
      const setParameters = stAny.stretch?.setParameters;
      if (typeof setParameters === 'function') {
        setParameters.call(
          stAny.stretch,
          input.sampleRate || context.sampleRate || DEFAULT_SAMPLE_RATE,
          HQ_SEQUENCE_MS,
          HQ_SEEK_WINDOW_MS,
          HQ_OVERLAP_MS
        );
      }
    }
    soundTouch.tempo = tempo;
    soundTouch.rate = 1;
    soundTouch.pitch = 1;

    const paddedInput = this.appendSilentTail(context, input, STRETCH_FLUSH_PAD_MS);
    const source = new WebAudioBufferSource(paddedInput);
    const filter = new SimpleFilter(source, soundTouch);
    const chunks: Float32Array[] = [];
    let totalFrames = 0;

    while (true) {
      const temp = new Float32Array(SOUND_TOUCH_CHUNK_FRAMES * 2);
      const extracted = filter.extract(temp, SOUND_TOUCH_CHUNK_FRAMES);
      if (!extracted || extracted <= 0) break;
      chunks.push(temp.subarray(0, extracted * 2));
      totalFrames += extracted;
    }

    if (totalFrames <= 0) {
      return input;
    }

    const sampleRate = input.sampleRate || context.sampleRate || DEFAULT_SAMPLE_RATE;
    const estimatedPadOutputFrames = Math.floor(((STRETCH_FLUSH_PAD_MS / 1000) * sampleRate) / tempo);
    const trailingGuardFrames = Math.floor((TRAILING_GUARD_MS / 1000) * sampleRate);
    const trimCapFrames = Math.max(0, estimatedPadOutputFrames - trailingGuardFrames);

    const output = context.createBuffer(
      Math.max(1, Math.min(2, input.numberOfChannels || 1)),
      totalFrames,
      sampleRate
    );

    const left = output.getChannelData(0);
    const right = output.numberOfChannels > 1 ? output.getChannelData(1) : null;
    let frameOffset = 0;

    for (const chunk of chunks) {
      const frameCount = chunk.length / 2;
      for (let i = 0; i < frameCount; i += 1) {
        left[frameOffset + i] = chunk[i * 2];
        if (right) {
          right[frameOffset + i] = chunk[i * 2 + 1];
        }
      }
      frameOffset += frameCount;
    }

    const trimmedFrames = this.trimTrailingSilenceFrames(output, trimCapFrames);
    if (trimmedFrames < output.length) {
      const trimmed = context.createBuffer(output.numberOfChannels, trimmedFrames, sampleRate);
      for (let ch = 0; ch < output.numberOfChannels; ch += 1) {
        trimmed
          .getChannelData(ch)
          .set(output.getChannelData(ch).subarray(0, trimmedFrames), 0);
      }
      return trimmed;
    }

    return output;
  }

  /**
   * 在输入末尾附加一段静音，降低 SoundTouch 尾部帧被“卡住不输出”的概率。
   */
  private appendSilentTail(
    context: AudioContext,
    input: AudioBuffer,
    padMs: number
  ): AudioBuffer {
    const sampleRate = input.sampleRate || context.sampleRate || DEFAULT_SAMPLE_RATE;
    const padFrames = Math.max(1, Math.floor((padMs / 1000) * sampleRate));
    const out = context.createBuffer(
      Math.max(1, Math.min(2, input.numberOfChannels || 1)),
      input.length + padFrames,
      sampleRate
    );
    const channelCount = out.numberOfChannels;
    for (let ch = 0; ch < channelCount; ch += 1) {
      const srcCh = Math.min(ch, Math.max(0, input.numberOfChannels - 1));
      out.getChannelData(ch).set(input.getChannelData(srcCh), 0);
    }
    return out;
  }

  /**
   * 仅在给定上限范围内裁掉“尾部纯静音”，用于去除补静音带来的额外尾巴。
   */
  private trimTrailingSilenceFrames(buffer: AudioBuffer, maxTrimFrames: number): number {
    if (maxTrimFrames <= 0 || buffer.length <= 1) {
      return buffer.length;
    }
    const channelCount = buffer.numberOfChannels;
    const tailScanStart = Math.max(0, buffer.length - maxTrimFrames);
    let lastNonSilentFrame = -1;

    for (let frame = buffer.length - 1; frame >= tailScanStart; frame -= 1) {
      let isNonSilent = false;
      for (let ch = 0; ch < channelCount; ch += 1) {
        if (Math.abs(buffer.getChannelData(ch)[frame]) > TRAILING_SILENCE_THRESHOLD) {
          isNonSilent = true;
          break;
        }
      }
      if (isNonSilent) {
        lastNonSilentFrame = frame;
        break;
      }
    }

    if (lastNonSilentFrame < 0) {
      return Math.max(1, tailScanStart);
    }

    const desiredLength = lastNonSilentFrame + 1;
    return Math.max(1, Math.min(buffer.length, desiredLength));
  }

  private async ensureContext(): Promise<AudioContext> {
    if (!this.context) {
      const AudioContextCtor =
        window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (!AudioContextCtor) {
        throw new Error('当前浏览器不支持 AudioContext');
      }

      this.context = new AudioContextCtor();
      this.gainNode = this.context.createGain();
      this.gainNode.gain.value = 1;
      this.gainNode.connect(this.context.destination);
    }

    return this.context;
  }

  private startSourceAt(time: number): void {
    if (!this.context || !this.gainNode || !this.masterBuffer) {
      return;
    }

    this.stopSource();

    const source = this.context.createBufferSource();
    source.buffer = this.masterBuffer;
    source.connect(this.gainNode);
    source.onended = () => {
      if (this.sourceNode === source) {
        this.playing = false;
        this.lastKnownOffset = this.masterBuffer?.duration || 0;
        this.sourceNode = null;
      }
    };

    source.start(0, time);
    this.sourceNode = source;
    this.playing = true;
    this.startContextTime = this.context.currentTime;
    this.startOffset = time;
    this.lastKnownOffset = time;
  }

  private stopSource(): void {
    if (!this.sourceNode) {
      return;
    }

    try {
      this.sourceNode.onended = null;
      this.sourceNode.stop();
      this.sourceNode.disconnect();
    } catch {
      // ignore stop errors
    }

    this.sourceNode = null;
  }

  private getFadeGain(rawRatio: number): number {
    const ratio = clamp(rawRatio, 0, 1);
    if (this.fadeCurve === 'linear') {
      return ratio;
    }
    return Math.sin((ratio * Math.PI) / 2);
  }
}
